import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getStoredToken } from '../../utils/tokenStorage';
import './SharedFileLanding.css';

interface SharedFileInfo {
  authenticated: boolean;
  // Layer 1: unauthenticated response (no file metadata leaked)
  requires_verification?: boolean;
  require_otp?: boolean;
  email_hint?: string;
  // Authenticated response
  filename?: string;
  molecule_count?: number;
  permission?: string;
  file_id?: number;
  file_status?: string;
  shared_by?: string;
  shared_by_name?: string;
  editor_url?: string;
  login_url?: string;
  register_url?: string;
}

const SharedFileLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, login, logout, registerGuest, branding } = useAuth();

  const [shareInfo, setShareInfo] = useState<SharedFileInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Mode: verify (OTP), register, or login
  const [mode, setMode] = useState<'verify' | 'register' | 'login'>('verify');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedMsg, setAccessDeniedMsg] = useState('');

  // Layer 2: OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [otpEmailHint, setOtpEmailHint] = useState('');
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timers
  useEffect(() => {
    if (otpExpiresIn <= 0) return;
    const timer = setInterval(() => setOtpExpiresIn((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(timer);
  }, [otpExpiresIn]);

  useEffect(() => {
    if (otpResendCooldown <= 0) return;
    const timer = setInterval(() => setOtpResendCooldown((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(timer);
  }, [otpResendCooldown]);

  // Fetch share info
  useEffect(() => {
    if (authLoading) return;

    const fetchShareInfo = async () => {
      try {
        const headers: Record<string, string> = {};
        const storedToken = getStoredToken();
        if (storedToken) {
          headers['Authorization'] = `Bearer ${storedToken}`;
        }
        const res = await fetch(`/api/v1/shared/${token}`, { headers });
        if (!res.ok) {
          const err = await res.json();
          if (res.status === 403 && err.detail?.includes('different email')) {
            setAccessDenied(true);
            setAccessDeniedMsg(err.detail);
            setLoading(false);
            return;
          }
          setError(err.detail || 'Share link not found');
          setLoading(false);
          return;
        }
        const data = await res.json();
        setShareInfo(data);

        // If OTP is not required by tenant policy, default to login tab
        if (data.require_otp === false && !data.authenticated) {
          setMode('login');
        }

        // If authenticated and we have the file_id, redirect to editor with share context
        if (data.authenticated && data.file_id) {
          navigate(`/chemistry/file/${data.file_id}/editor`, {
            replace: true,
            state: {
              shareToken: token,
              sharedByName: data.shared_by_name,
              permission: data.permission,
            },
          });
          return;
        }
      } catch {
        setError('Failed to load shared file');
      }
      setLoading(false);
    };

    fetchShareInfo();
  }, [token, authLoading, isAuthenticated, navigate]);

  // ── Layer 2: OTP handlers ──

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    setSubmitting(true);

    try {
      const res = await fetch(`/api/v1/shared/${token}/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        setOtpError(data.detail || 'Failed to send verification code');
        setSubmitting(false);
        return;
      }
      setOtpSent(true);
      setOtpEmailHint(data.email_hint || '');
      setOtpExpiresIn(data.expires_in_seconds || 600);
      setOtpResendCooldown(60);
      setOtpCode(['', '', '', '', '', '']);
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    } catch {
      setOtpError('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  const handleOtpDigit = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const newCode = [...otpCode];
    newCode[index] = value;
    setOtpCode(newCode);

    // Auto-advance to next field
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newCode.every((d) => d !== '')) {
      handleVerifyOtp(newCode.join(''));
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setOtpCode(newCode);
      handleVerifyOtp(pasted);
    }
  };

  const handleVerifyOtp = async (code?: string) => {
    const codeStr = code || otpCode.join('');
    if (codeStr.length !== 6) return;

    setOtpError('');
    setSubmitting(true);

    try {
      const res = await fetch(`/api/v1/shared/${token}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: codeStr }),
      });
      const data = await res.json();
      if (!res.ok) {
        setOtpError(data.detail || 'Verification failed');
        setOtpCode(['', '', '', '', '', '']);
        otpInputRefs.current[0]?.focus();
        setSubmitting(false);
        return;
      }

      // Redirect to editor with share context
      if (data.file_id) {
        navigate(`/chemistry/file/${data.file_id}/editor`, {
          replace: true,
          state: {
            shareToken: token,
            sharedByName: data.shared_by_name,
            permission: data.permission,
          },
        });
      }
    } catch {
      setOtpError('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  // ── Existing auth handlers ──

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    const result = await registerGuest(name, email, password, token);
    if (result.success) {
      if (result.sharedFile) {
        navigate(`/chemistry/file/${result.sharedFile.file_id}/editor`, {
          replace: true,
          state: { shareToken: token },
        });
      } else {
        navigate(`/shared/${token}`, { replace: true });
        window.location.reload();
      }
    } else {
      setFormError(result.error || 'Registration failed');
      setSubmitting(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    const result = await login(email, password);
    if (result.success) {
      window.location.reload();
    } else {
      setFormError(result.error || 'Login failed');
      setSubmitting(false);
    }
  };

  // ── Render states ──

  if (loading || authLoading) {
    return (
      <div className="shared-landing">
        <div className="shared-landing__loader">
          <div className="shared-landing__spinner" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shared-landing">
        <div className="shared-landing__card">
          <div className="shared-landing__error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2 className="shared-landing__title">Link Unavailable</h2>
          <p className="shared-landing__subtitle">{error}</p>
          <button
            className="shared-landing__btn shared-landing__btn--secondary"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Layer 1: Access denied — email mismatch
  if (accessDenied) {
    return (
      <div className="shared-landing">
        <div className="shared-landing__card">
          <div className="shared-landing__error-icon" style={{ color: '#f59e0b' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v4m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
            </svg>
          </div>
          <h2 className="shared-landing__title">Access Denied</h2>
          <p className="shared-landing__subtitle">{accessDeniedMsg}</p>
          <button
            className="shared-landing__btn shared-landing__btn--secondary"
            onClick={() => {
              logout();
              setAccessDenied(false);
              setAccessDeniedMsg('');
              window.location.reload();
            }}
          >
            Sign in with a different account
          </button>
        </div>
      </div>
    );
  }

  if (!shareInfo) return null;

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="shared-landing">
      <div className="shared-landing__card shared-landing__card--wide">
        {/* Header with branding */}
        <div className="shared-landing__header">
          <div className="shared-landing__logo">
            <svg viewBox="0 0 40 40">
              <defs>
                <linearGradient id="shareLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={branding.primary_color} />
                  <stop offset="100%" stopColor={branding.secondary_color} />
                </linearGradient>
              </defs>
              <circle cx="20" cy="12" r="6" fill="url(#shareLogoGrad)" />
              <circle cx="10" cy="28" r="5" fill="url(#shareLogoGrad)" opacity="0.8" />
              <circle cx="30" cy="28" r="5" fill="url(#shareLogoGrad)" opacity="0.8" />
              <line x1="20" y1="12" x2="10" y2="28" stroke="url(#shareLogoGrad)" strokeWidth="2" />
              <line x1="20" y1="12" x2="30" y2="28" stroke="url(#shareLogoGrad)" strokeWidth="2" />
              <line x1="10" y1="28" x2="30" y2="28" stroke="url(#shareLogoGrad)" strokeWidth="2" />
            </svg>
          </div>
          <h1 className="shared-landing__brand">{branding.name}</h1>
        </div>

        {/* Secure invite message — no file details leaked (Layer 1) */}
        <div className="shared-landing__file-info">
          <div className="shared-landing__file-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
          </div>
          <div>
            <h2 className="shared-landing__filename">You've been invited to view a file</h2>
            <p className="shared-landing__file-meta">
              Sign in to access this shared molecular data
              {shareInfo.email_hint && (
                <span className="shared-landing__permission">
                  {' '}&middot; Sent to {shareInfo.email_hint}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="shared-landing__divider" />

        {/* Mode tabs: Verify Email (if OTP required) | Create Account | Log in */}
        <div className="shared-landing__tabs">
          {shareInfo?.require_otp !== false && (
            <button
              className={`shared-landing__tab ${mode === 'verify' ? 'shared-landing__tab--active' : ''}`}
              onClick={() => { setMode('verify'); setFormError(''); setOtpError(''); }}
            >
              Verify Email
            </button>
          )}
          <button
            className={`shared-landing__tab ${mode === 'register' ? 'shared-landing__tab--active' : ''}`}
            onClick={() => { setMode('register'); setFormError(''); setOtpError(''); }}
          >
            Create Account
          </button>
          <button
            className={`shared-landing__tab ${mode === 'login' ? 'shared-landing__tab--active' : ''}`}
            onClick={() => { setMode('login'); setFormError(''); setOtpError(''); }}
          >
            Log in
          </button>
        </div>

        {/* ── Layer 2: OTP Verification ── */}
        {mode === 'verify' && !otpSent && (
          <form className="shared-landing__form" onSubmit={handleRequestOtp}>
            {otpError && <div className="shared-landing__form-error">{otpError}</div>}
            <p className="shared-landing__hint" style={{ marginBottom: 8, color: 'var(--text-muted)' }}>
              Enter the email address this file was shared with. We'll send a one-time verification code.
            </p>
            <input
              type="email"
              className="shared-landing__input"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
            />
            <button
              type="submit"
              className="shared-landing__btn shared-landing__btn--primary"
              disabled={submitting}
              style={{
                background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`
              }}
            >
              {submitting ? 'Sending...' : 'Send Verification Code'}
            </button>
          </form>
        )}

        {mode === 'verify' && otpSent && (
          <div className="shared-landing__form">
            {otpError && <div className="shared-landing__form-error">{otpError}</div>}
            <p className="shared-landing__hint" style={{ marginBottom: 12, color: 'var(--text-muted)' }}>
              We sent a 6-digit code to {otpEmailHint || email}.
              {otpExpiresIn > 0 && (
                <span style={{ color: 'var(--text-secondary)' }}> Expires in {formatTime(otpExpiresIn)}</span>
              )}
            </p>

            {/* 6-digit OTP input */}
            <div className="shared-landing__otp-inputs" onPaste={handleOtpPaste}>
              {otpCode.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { otpInputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="shared-landing__otp-digit"
                  value={digit}
                  onChange={(e) => handleOtpDigit(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  autoFocus={i === 0}
                />
              ))}
            </div>

            <button
              className="shared-landing__btn shared-landing__btn--primary"
              disabled={submitting || otpCode.join('').length !== 6}
              onClick={() => handleVerifyOtp()}
              style={{
                background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`
              }}
            >
              {submitting ? 'Verifying...' : 'Verify & View File'}
            </button>

            <button
              className="shared-landing__btn shared-landing__btn--secondary"
              disabled={otpResendCooldown > 0}
              onClick={handleRequestOtp as any}
              style={{ marginTop: 4 }}
            >
              {otpResendCooldown > 0
                ? `Resend code in ${otpResendCooldown}s`
                : 'Resend code'}
            </button>
          </div>
        )}

        {/* Registration form */}
        {mode === 'register' && (
          <form className="shared-landing__form" onSubmit={handleRegister}>
            {formError && <div className="shared-landing__form-error">{formError}</div>}
            <input
              type="text"
              className="shared-landing__input"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
            />
            <input
              type="email"
              className="shared-landing__input"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <input
              type="password"
              className="shared-landing__input"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
            <button
              type="submit"
              className="shared-landing__btn shared-landing__btn--primary"
              disabled={submitting}
              style={{
                background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`
              }}
            >
              {submitting ? 'Creating account...' : 'View File'}
            </button>
            <p className="shared-landing__hint">Free account. No credit card required. 30 seconds.</p>
          </form>
        )}

        {/* Login form */}
        {mode === 'login' && (
          <form className="shared-landing__form" onSubmit={handleLogin}>
            {formError && <div className="shared-landing__form-error">{formError}</div>}
            <input
              type="email"
              className="shared-landing__input"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <input
              type="password"
              className="shared-landing__input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <button
              type="submit"
              className="shared-landing__btn shared-landing__btn--primary"
              disabled={submitting}
              style={{
                background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`
              }}
            >
              {submitting ? 'Signing in...' : 'Sign in & View File'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default SharedFileLanding;
